import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import PropertyDetails from "./pages/PropertyDetails";
import { InvestorFormProvider } from "./contexts/InvestorFormContext";
import InvestorFormModal from "./components/InvestorFormModal";
import Resources from "./pages/Resources";
import AllProperties from "./pages/AllProperties";
import Contact from "./pages/Contact";
import ScrollToHash from "./components/ScrollToHash";
import PrivacyPolicyPage from "./pages/PrivacyPolicy";
import FAQ from "./components/FAQ";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import AdminRoute from "./components/AdminRoute";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminPropertyForm from "./pages/admin/AdminPropertyForm";

// Books — merged from the standalone books-react app. Uses its own
// Supabase project/client and its own admin auth, entirely separate from
// multi-iq's property listings and property admin above.
import { AdminAuthProvider as BooksAdminAuthProvider } from "./books/context/AdminAuthContext";
import BooksAdminRoute from "./books/components/AdminRoute";
import BooksHomePage from "./books/pages/BooksHomePage";
import BookLandingPage from "./books/pages/BookLandingPage";
import BookReaderPage from "./books/pages/BookReaderPage";
import BooksAdminLoginPage from "./books/pages/admin/AdminLoginPage";
import BooksAdminLayout from "./books/pages/admin/AdminLayout";
import BooksAdminBooksPage from "./books/pages/admin/AdminBooksPage";
import BooksAdminLeadsPage from "./books/pages/admin/AdminLeadsPage";
import BooksNewBookPage from "./books/pages/admin/NewBookPage";
import BooksEditBookPage from "./books/pages/admin/EditBookPage";

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AdminAuthProvider>
        <BooksAdminAuthProvider>
          <InvestorFormProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <InvestorFormModal />
              <BrowserRouter>
                <ScrollToHash />
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/property/:id" element={<PropertyDetails />} />
                  <Route path="/properties" element={<AllProperties />} />
                  <Route path="/resources" element={<Resources />} />
                  <Route path="/contact-us" element={<Contact />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/faqs" element={<FAQ />} />
                  {/* Hidden admin: not linked from public site */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                    <Route index element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="properties/new" element={<AdminPropertyForm />} />
                    <Route path="properties/:id/edit" element={<AdminPropertyForm />} />
                  </Route>

                  {/* Books — public pages */}
                  <Route path="/books" element={<BooksHomePage />} />
                  <Route path="/books/:slug" element={<BookLandingPage />} />
                  <Route path="/books/:slug/read" element={<BookReaderPage />} />

                  {/* Books — separate admin portal, not linked from public site */}
                  <Route path="/admin/books-portal/login" element={<BooksAdminLoginPage />} />
                  <Route
                    path="/admin/books-portal"
                    element={
                      <BooksAdminRoute>
                        <BooksAdminLayout />
                      </BooksAdminRoute>
                    }
                  >
                    <Route index element={<BooksAdminBooksPage />} />
                    <Route path="leads" element={<BooksAdminLeadsPage />} />
                    <Route path="books/new" element={<BooksNewBookPage />} />
                    <Route path="books/:id/edit" element={<BooksEditBookPage />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </InvestorFormProvider>
        </BooksAdminAuthProvider>
      </AdminAuthProvider>
    </QueryClientProvider>
  );
};

export default App;
